import { KanbanBoard, KanbanColumn } from '@/components/kanban'
import type { KanbanColumnData } from '@/components/kanban'
import { SubmissionCard } from './SubmissionCard'
import {
  CURATION_COLUMNS,
  type CurationColumnId,
  type Submission,
} from '../hooks/useCurationSubmissions'

interface Props {
  grouped: Map<CurationColumnId, Submission[]>
  onMove: (submissionId: string, toColumn: CurationColumnId, toIndex: number) => void
  onNotesChange: (submissionId: string, notes: string) => void
  onOpenDetail: (submission: Submission) => void
}

const ACCENT_BORDER: Record<string, string> = {
  neutral: 'border-border',
  success: 'border-emerald-500/40',
  warning: 'border-amber-500/40',
  destructive: 'border-destructive/40',
}

export function CurationBoardView({ grouped, onMove, onNotesChange, onOpenDetail }: Props) {
  const columns: KanbanColumnData<Submission>[] = CURATION_COLUMNS.map((c) => ({
    id: c.id,
    title: c.title,
    cards: grouped.get(c.id) ?? [],
    ordered: c.ordered,
    accent: c.accent,
  }))

  return (
    <KanbanBoard
      columns={columns}
      onCardMove={({ cardId, toColumnId, toIndex }) =>
        onMove(cardId, toColumnId as CurationColumnId, toIndex)
      }
      renderColumn={(col) => {
        const config = CURATION_COLUMNS.find((c) => c.id === col.id)!
        return (
          <KanbanColumn key={col.id} column={col}>
            {({ droppableRef, isOver }) => (
              <div
                className={`flex w-72 shrink-0 flex-col rounded-lg border-2 bg-muted/30 ${
                  ACCENT_BORDER[config.accent]
                }`}
              >
                <div className="flex items-center justify-between border-b px-3 py-2">
                  <h3 className="text-sm font-semibold">{col.title}</h3>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {col.cards.length}
                  </span>
                </div>
                <div
                  ref={droppableRef}
                  className={`flex flex-1 flex-col gap-2 p-2 transition-colors ${
                    isOver ? 'bg-accent/30' : ''
                  }`}
                >
                  {col.cards.map((s, i) => (
                    <SubmissionCard
                      key={s.id}
                      submission={s}
                      rankIndex={config.ordered ? i + 1 : undefined}
                      onNotesChange={(n) => onNotesChange(s.id, n)}
                      onOpenDetail={() => onOpenDetail(s)}
                    />
                  ))}
                  {col.cards.length === 0 && (
                    <div className="rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            )}
          </KanbanColumn>
        )
      }}
    />
  )
}
